import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, MessageCircle, Send, RefreshCw, Volume2, VolumeX, Bot } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import BottomNav from "../components/BottomNav";

const SupportPage = () => {
  const { user, authAxios } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);  // Default: voice on
  const [playingAudio, setPlayingAudio] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    // Welcome message
    setMessages([{
      sender: "ai",
      message: "Namaste! Main Lite Gold AI Assistant hoon. Aap mujhse Lite Gold ke baare mein kuch bhi pooch sakte ho! 🚀",
      timestamp: new Date().toISOString()
    }]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const playAudio = (base64Audio) => {
    if (!base64Audio || !voiceEnabled) return;
    
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      setPlayingAudio(true);
      
      audio.onended = () => setPlayingAudio(false);
      audio.onerror = () => setPlayingAudio(false);
      audio.play().catch(() => setPlayingAudio(false));
    } catch (e) {
      console.error("Audio play error:", e);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    const userMsg = {
      sender: "user",
      message: newMessage.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setNewMessage("");
    setSending(true);

    try {
      const api = authAxios();
      const endpoint = voiceEnabled ? "/ai-support/chat-voice" : "/ai-support/chat";
      
      const res = await api.post(endpoint, {
        message: userMsg.message,
        session_id: sessionId
      });

      if (res.data.success) {
        setSessionId(res.data.session_id);
        
        const aiMsg = {
          sender: "ai",
          message: res.data.response,
          timestamp: new Date().toISOString(),
          audio: res.data.audio_base64
        };
        
        setMessages(prev => [...prev, aiMsg]);
        
        // Play voice if enabled
        if (voiceEnabled && res.data.audio_base64) {
          playAudio(res.data.audio_base64);
        }
      } else {
        throw new Error(res.data.error || "AI response failed");
      }
    } catch (e) {
      console.error("AI Chat error:", e);
      const errorMsg = {
        sender: "ai",
        message: "Sorry, kuch technical issue hai. Please thodi der baad try karein.",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
      toast.error("AI response failed");
    }
    
    setSending(false);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickQuestions = [
    "Lite Gold kaise kaam karta hai?",
    "Withdrawal mein delay kyun?",
    "Referral bonus kaise milega?",
    "Earning kaise hoti hai?"
  ];

  return (
    <div className="screen-container flex flex-col" style={{height: 'calc(100vh - 70px)'}} data-testid="support-page">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center gap-3 shrink-0 bg-gradient-to-r from-primary/20 to-purple-500/20">
        <button onClick={() => navigate("/dashboard")} className="p-2">
          <ChevronLeft size={24} />
        </button>
        <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center">
          <Bot size={24} className="text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="font-bold text-lg flex items-center gap-2">
            Lite Gold AI
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          </h1>
          <p className="text-xs text-muted-foreground">24/7 AI Support • Always Online</p>
        </div>
        <button 
          onClick={() => {
            stopAudio();
            setVoiceEnabled(!voiceEnabled);
          }}
          className={`p-2 rounded-full ${voiceEnabled ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}
        >
          {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl p-3 ${
              msg.sender === "user" 
                ? "bg-primary text-primary-foreground rounded-br-sm" 
                : "bg-gradient-to-br from-card to-primary/10 border border-primary/20 rounded-bl-sm"
            }`}>
              {msg.sender === "ai" && (
                <div className="flex items-center gap-2 mb-1">
                  <Bot size={14} className="text-primary" />
                  <span className="text-xs text-primary font-medium">Lite Gold AI</span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              <div className="flex items-center justify-between mt-1">
                <p className={`text-xs ${msg.sender === "user" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {formatTime(msg.timestamp)}
                </p>
                {msg.audio && voiceEnabled && (
                  <button 
                    onClick={() => playAudio(msg.audio)}
                    className="text-primary hover:text-primary/80"
                    disabled={playingAudio}
                  >
                    <Volume2 size={14} className={playingAudio ? "animate-pulse" : ""} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {sending && (
          <div className="flex justify-start">
            <div className="bg-card border border-border rounded-2xl rounded-bl-sm p-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto shrink-0">
          {quickQuestions.map((q, idx) => (
            <button
              key={idx}
              onClick={() => setNewMessage(q)}
              className="whitespace-nowrap px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-full text-xs text-primary hover:bg-primary/20"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border bg-background shrink-0">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Kuch bhi poocho..."
            className="flex-1 input-dark rounded-full px-4 py-3"
            disabled={sending}
            data-testid="support-message-input"
          />
          <button 
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="p-3 bg-primary rounded-full disabled:opacity-50 hover:bg-primary/90 transition-colors"
            data-testid="support-send-btn"
          >
            {sending ? (
              <RefreshCw size={20} className="animate-spin text-white" />
            ) : (
              <Send size={20} className="text-white" />
            )}
          </button>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-2">
          {voiceEnabled ? "🔊 Voice enabled - AI will speak responses" : "💬 Text mode - Tap speaker icon to enable voice"}
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default SupportPage;
